/**
 * Typed claims API client. JSDoc types reference operations declared in
 * `generated.d.ts` (regenerated via `npm run api:gen` from `claims/openapi.yaml`).
 *
 * Translation between the prototype's UI shape and the wire shape lives at
 * the bottom of this file (`fromUiClaim` / `toUiClaim`) per spec
 * § "Translation layer".
 */

import api from '../utils/axios';

/** @typedef {import('./generated').paths} Paths */
/** @typedef {import('./generated').components['schemas']} Schemas */
/** @typedef {Schemas['Claim']} ClaimDetail */

// ─── Claim CRUD ──────────────────────────────────────────────────────────────

export async function createDraft() {
  const { data } = await api.post('/claims/draft/');
  return data;
}

/**
 * @param {string} id
 * @param {{ include_validation?: boolean }} [opts]
 */
export async function fetchClaim(id, opts = {}) {
  const params = {};
  if (opts.include_validation) params.include_validation = true;
  const { data } = await api.get(`/claims/${id}/`, { params });
  return data;
}

/**
 * @param {string} id
 * @param {object} body — must include `version`.
 */
export async function patchClaim(id, body) {
  const { data } = await api.patch(`/claims/${id}/`, body);
  return data;
}

/** @param {string} id */
export async function validateClaim(id) {
  const { data } = await api.post(`/claims/${id}/validate/`);
  return data;
}

/**
 * @param {string} id
 * @param {{ idempotency_key: string, channel: 'office_ally'|'fax'|'export', fax_number?: string }} body
 */
export async function submitClaim(id, body) {
  const { data } = await api.post(`/claims/${id}/submit/`, body);
  return data;
}

/**
 * @param {string} id
 * @param {{ idempotency_key: string, channel: 'office_ally'|'fax'|'export', fax_number?: string }} body
 */
export async function resubmitClaim(id, body) {
  const { data } = await api.post(`/claims/${id}/resubmit/`, body);
  return data;
}

/**
 * Creates an editable copy of a (typically submitted) claim.
 * @param {string} id
 */
export async function cloneClaim(id) {
  const { data } = await api.post(`/claims/${id}/clone/`);
  return data;
}

/** @param {string} id */
export async function deleteClaim(id) {
  await api.delete(`/claims/${id}/`);
}

/**
 * Reopen a submitted claim for editing — returns to draft state.
 * @param {string} id
 */
export async function reopenClaim(id) {
  const { data } = await api.post(`/claims/${id}/reopen-for-editing/`);
  return data;
}

/**
 * Manually mark a validated claim as submitted without a delivery action.
 * @param {string} id
 */
export async function markClaimSubmitted(id) {
  const { data } = await api.post(`/claims/${id}/mark-submitted/`);
  return data;
}

/**
 * @param {object} [params]
 */
export async function listClaims(params = {}) {
  const { data } = await api.get('/claims/', { params });
  return asList(data);
}

/**
 * Overview-dashboard action-item counts for the logged-in user.
 * Always scoped to request.user on the backend (admin not widened).
 * @returns {Promise<{counts: {draft: number, denied_unappealed: number, rejected: number, total: number}}>}
 */
export async function getActionItems() {
  const { data } = await api.get('/claims/action_items/');
  return data;
}

// ─── Patients ────────────────────────────────────────────────────────────────

/**
 * Normalize a list endpoint response: unwrap DRF-paginated
 * `{count, next, previous, results}` into the underlying array, and coerce
 * unexpected shapes (e.g., error bodies) to []. List consumers always get
 * an array regardless of pagination state on the backend.
 * @param {any} data
 */
function asList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

export async function listPatients() {
  const { data } = await api.get('/patients/');
  return asList(data);
}

/**
 * Server-side search; backend `search_fields` = first/last/email/phone/ssn/dob.
 * @param {string} query
 */
export async function searchPatients(query) {
  const { data } = await api.get('/patients/', { params: { q: query } });
  return asList(data);
}

/** @param {object} form */
export async function createPatient(form) {
  const { data } = await api.post('/patients/', form);
  return data;
}

/**
 * Tracking projection for one submission attempt.
 *
 * Returns the backend's derived lifecycle — stages with per-channel availability,
 * normalized events, and a current state — NOT raw rows. The frontend must not
 * reverse-engineer lifecycle semantics from ClaimSubmission/ClaimResponse; that is
 * what this endpoint exists to prevent.
 *
 * @param {string} claimId
 * @param {string} [submissionId] Attempt to render; defaults to the latest.
 */
export async function fetchClaimTracking(claimId, submissionId) {
  const params = submissionId ? { submission_id: submissionId } : undefined;
  const { data } = await api.get(`/claims/${claimId}/tracking/`, { params });
  return data;
}

/**
 * Queue ONE manual per-attempt ERA (electronic remittance) fetch on Claim.MD.
 *
 * On success (202) resolves to `{ state, submission_id }`. On refusal the
 * shared axios instance rejects with the backend's 400/409/422/502 response
 * still attached at `error.response.data` (`{ reason, retry_after_seconds? }`)
 * — callers read `reason` rather than inventing their own copy, since it is a
 * closed code from `claims/services/era_policy.py`.
 *
 * @param {string} claimId
 * @param {string} submissionId
 * @returns {Promise<{ state: string, submission_id: string }>}
 */
export async function requestEraFetch(claimId, submissionId) {
  const { data } = await api.post(`/claims/${claimId}/tracking/era-fetch/`, { submission_id: submissionId });
  return data;
}

/**
 * Download the ERA remittance PDF bound to one attempt, as an object URL.
 * Mirrors downloadLMNPdf's pattern above: the endpoint is authenticated, so a
 * bare `<a href>` against the raw path would 401 — JWT is attached only by
 * the axios interceptor (frontend/src/utils/axios.js), never by browser
 * navigation. Fetching the blob through the shared `api` instance and handing
 * back `URL.createObjectURL(...)` lets a caller use the result as a real
 * `href` (so the link stays keyboard/right-click accessible) while the actual
 * navigation never leaves the page.
 *
 * The caller must revoke the returned URL after use
 * (`window.URL.revokeObjectURL`) once the download has been triggered.
 *
 * @param {string} claimId
 * @param {string} artifactId
 * @returns {Promise<string>}
 */
export async function eraArtifactUrl(claimId, artifactId) {
  const { data } = await api.get(`/claims/${claimId}/tracking/era-artifact/${artifactId}/`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
}

// ─── Payers ──────────────────────────────────────────────────────────────────

export async function listPayers() {
  const { data } = await api.get('/claims/payers/');
  return asList(data);
}

/** @param {string} query */
export async function searchPayers(query) {
  const { data } = await api.get('/claims/office-ally-payers/search/', { params: { q: query } });
  return asList(data);
}

/** @param {string} query */
export async function searchClaimMdPayers(query) {
  const { data } = await api.get('/claims/claim-md-payers/search/', { params: { q: query } });
  return asList(data);
}

/**
 * Search both clearinghouse payer catalogs and merge them into one list.
 *
 * The two catalogs overlap heavily — 2,295 payer_ids are present in both — so
 * concatenating them would render the same payer twice and, because the picker
 * keys rows by payer_id, produce duplicate React keys. Deduplicate on a
 * normalized payer_id (trimmed, upper-cased: the compatibility endpoint matches
 * case-insensitively, so "uhcempire" and "UHCEMPIRE" are one payer).
 *
 * Office Ally wins a collision. It is the incumbent source, its records carry
 * `capabilities`, and keeping it means payers that were already selectable
 * continue to behave exactly as before — this only ever ADDS Claim.MD-only
 * payers to the list.
 *
 * One catalog failing must not empty the picker: a reference-data outage should
 * degrade the list, not strand the user mid-claim. Only a total failure of both
 * rejects, so callers can still surface a real error.
 *
 * @param {string} query
 * @returns {Promise<any[]>} merged payers, Office Ally first, then Claim.MD-only
 */
export async function searchAllPayers(query) {
  const [officeAlly, claimMd] = await Promise.allSettled([
    searchPayers(query),
    searchClaimMdPayers(query),
  ]);

  if (officeAlly.status === 'rejected' && claimMd.status === 'rejected') {
    throw officeAlly.reason;
  }

  const normalize = (/** @type {any} */ payerId) => String(payerId ?? '').trim().toUpperCase();

  const merged = [];
  const seen = new Set();
  // Office Ally first so it claims the payer_id before Claim.MD is considered.
  for (const source of [officeAlly, claimMd]) {
    if (source.status !== 'fulfilled') continue;
    for (const payer of source.value || []) {
      const key = normalize(payer?.payer_id);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(payer);
    }
  }
  return merged;
}

/**
 * Ask whether a payer ID can receive a professional claim through Claim.MD.
 * Always resolves for a well-formed payer ID — an unlisted payer comes back as
 * `{ supported: false, reason: 'not_listed' }` rather than a 404.
 * @param {string} payerId
 * @returns {Promise<{
 *   payer_id: string,
 *   supported: boolean,
 *   reason: 'not_listed'|'no_professional_claims'|null,
 *   message: string,
 *   supports_attachments: boolean,
 *   payer: any|null,
 * }>}
 */
export async function checkClaimMdPayerCompatibility(payerId) {
  const { data } = await api.get('/claims/claim-md-payers/compatibility/', { params: { payer_id: payerId } });
  return data;
}

// ─── Reference data ──────────────────────────────────────────────────────────

/**
 * @param {{ is_default?: boolean }} [opts]
 */
export async function listBillingProviders(opts = {}) {
  const params = {};
  if (opts.is_default !== undefined) params.is_default = String(opts.is_default);
  const { data } = await api.get('/claims/billing-providers/', { params });
  return asList(data);
}

/** @param {object} form */
export async function createBillingProvider(form) {
  const { data } = await api.post('/claims/billing-providers/', form);
  return data;
}

/** @param {string} profileId @param {object} form */
export async function updateBillingProvider(profileId, form) {
  const { data } = await api.patch(`/claims/billing-providers/${profileId}/`, form);
  return data;
}

/** @param {string} profileId */
export async function deleteBillingProvider(profileId) {
  await api.delete(`/claims/billing-providers/${profileId}/`);
}

/**
 * Set the claim's tied billing provider (RECON manual override, Slice C1).
 * Tenant-scoped on the backend. Returns the refreshed ClaimDetail.
 * @param {string} claimId @param {string} profileId
 */
export async function setClaimBillingProvider(claimId, profileId) {
  const { data } = await api.post(`/claims/${claimId}/billing-provider/`, {
    billing_provider_profile_id: profileId,
  });
  return data;
}

// ─── Documents ───────────────────────────────────────────────────────────────

/**
 * @param {string} claimId
 * @param {File} file
 * @param {{ document_type?: string, original_filename?: string }} [meta]
 * @returns {Promise<object>} ClaimSupportingDocument shape.
 */
export async function uploadDocument(claimId, file, meta = {}) {
  const fd = new FormData();
  fd.append('file', file);
  if (meta.document_type) fd.append('document_type', meta.document_type);
  if (meta.original_filename) fd.append('original_filename', meta.original_filename);
  const { data } = await api.post(`/claims/${claimId}/upload_document/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** @param {string} claimId @param {string} attachmentId */
export async function deleteClaimDocument(claimId, attachmentId) {
  await api.delete(`/claims/${claimId}/documents/${attachmentId}/`);
}

/** @param {string} claimId */
export async function listClaimDocuments(claimId) {
  const { data } = await api.get(`/claims/${claimId}/documents/`);
  return asList(data);
}

// Attach an existing UploadedDocument (e.g. one picked from the Patient Library,
// which is patient-wide and may belong to another of the patient's claims) to
// THIS claim. The appeal submit path requires documents to be attached to the
// claim, so the UI calls this before including a library doc in a submission.
export async function attachDocumentToClaim(claimId, documentId) {
  const { data } = await api.post(`/claims/${claimId}/attach_document/`, { document_id: documentId });
  return data;
}

// ─── Appeal letters ─────────────────────────────────────────────────────────

export async function listAppealTemplates() {
  const { data } = await api.get('/documents/appeal-templates/');
  return asList(data?.templates || data);
}

export async function getAppealTemplateConfig() {
  const { data } = await api.get('/documents/appeal-templates/');
  return {
    templates: asList(data?.templates || data),
    denial_categories: asList(data?.denial_categories),
  };
}

/**
 * Submit the existing async appeal-generation workflow.
 * @param {{ claimId: string, templateId: string, model?: string }} options
 */
export async function generateAppealLetter({ claimId, templateId, model = 'gemini-2.5-flash', section_keys }) {
  const payload = {
    claim_id: claimId,
    template_id: templateId,
    model,
  };
  // Optional per-section regenerate (plan 06-09): the backend AppealLetterView
  // validates section_keys against APPEAL_SECTION_KEYS and only regenerates
  // those sections, leaving provider edits on the others intact.
  if (Array.isArray(section_keys) && section_keys.length > 0) {
    payload.section_keys = section_keys;
  }
  const { data } = await api.post('/documents/appeal/generate/', payload);
  return data;
}

/** @param {string} claimId */
export async function listClaimAppeals(claimId) {
  const { data } = await api.get(`/documents/claims/${claimId}/appeals/`);
  return asList(data?.appeals || data);
}

/**
 * PATCH /api/v1/documents/appeals/<appealId>/ with updated content.
 * Persists appeal_content and triggers PDF re-render on the backend.
 * @param {string} appealId
 * @param {string} content
 */
export async function updateAppealContent(appealId, content) {
  const { data } = await api.patch(`/documents/appeals/${appealId}/`, { appeal_content: content });
  return data;
}

// ─── LMN async pipeline ──────────────────────────────────────────────────────
// Two-call sequence: createLMNLetter creates a draft (no Pub/Sub),
// generateLMNLetter publishes the lmn_generate Pub/Sub job.

/** @param {string} claimId */
export async function createLMNLetter(claimId) {
  const { data } = await api.post('/documents/medical-necessity-letters/', { claim_id: claimId });
  return data;
}

/**
 * @param {string} letterId
 * @param {{ clinical_notes?: string, custom_instructions?: string }} body
 */
export async function generateLMNLetter(letterId, body) {
  const { data } = await api.post(
    `/documents/medical-necessity-letters/${letterId}/generate/`,
    body,
  );
  return data;
}

/** @param {string} letterId */
export async function getLMNLetter(letterId) {
  const { data } = await api.get(`/documents/medical-necessity-letters/${letterId}/`);
  return data;
}

/**
 * Persist hand-edited LMN content. The backend re-renders the PDF inline and
 * updates the same UploadedDocument row, so the LMN attachment stays stable.
 *
 * @param {string} letterId
 * @param {string} generatedContent
 */
export async function updateLMNLetterContent(letterId, generatedContent) {
  const { data } = await api.patch(
    `/documents/medical-necessity-letters/${letterId}/content/`,
    { generated_content: generatedContent },
  );
  return data;
}

/**
 * Download the rendered LMN PDF as a Blob via the shared, JWT-authed api
 * instance. Mirrors fetchPreviewPdf's pattern below. A bare window.open
 * against the export URL would 401 because JWT auth is attached only by
 * the axios interceptor (frontend/src/utils/axios.js).
 *
 * Use `export_format`, NOT `format`. DRF's default content negotiation
 * interprets `?format=…` as a renderer-suffix override and raises Http404
 * (returned as `{"detail":"Not found."}`) when no renderer matches `pdf`,
 * which it never does. The backend reads `export_format` to avoid the
 * collision — see documents/views.py::MedicalNecessityLetterExportView.
 *
 * @param {string} letterId
 * @returns {Promise<Blob>}
 */
export async function downloadLMNPdf(letterId) {
  const { data } = await api.get(
    `/documents/medical-necessity-letters/${letterId}/export/`,
    { params: { export_format: 'pdf' }, responseType: 'blob' },
  );
  return data;
}

/** @param {string} letterId */
export async function deleteLMNLetter(letterId) {
  await api.delete(`/documents/medical-necessity-letters/${letterId}/`);
}

// ─── Preview PDF ─────────────────────────────────────────────────────────────

/**
 * @param {string} claimId
 * @returns {Promise<Blob>}
 */
export async function fetchPreviewPdf(claimId) {
  const { data } = await api.get(`/claims/${claimId}/preview-pdf/`, {
    responseType: 'blob',
  });
  return data;
}

/**
 * Returns the absolute URL for the CMS-1500 preview PDF endpoint.
 * Direct iframe/link use cannot attach bearer auth; prefer fetchPreviewPdf for
 * protected previews.
 * @param {string} claimId
 */
export function getPreviewPdfUrl(claimId) {
  const base = (api.defaults && api.defaults.baseURL) || '';
  return `${base.replace(/\/+$/, '')}/claims/${claimId}/preview-pdf/`;
}

// ─── Translation layer ───────────────────────────────────────────────────────
// Spec mapping table: § Field mapping. UI shape (prototype's flat
// box-numbered) <-> wire shape (claims/openapi.yaml).

/** @type {Record<string, string>} */
const REL_WIRE_TO_UI = { '18': 'self', '01': 'spouse', '19': 'child', G8: 'other' };
/** @type {Record<string, string>} */
const REL_UI_TO_WIRE = { self: '18', spouse: '01', child: '19', other: 'G8' };

/** @type {Record<string, string>} */
const FREQ_WIRE_TO_UI = { '1': 'new', '7': 'corrected', '8': 'rejected' };
/** @type {Record<string, string>} */
const FREQ_UI_TO_WIRE = { new: '1', corrected: '7', rejected: '8' };

/** @type {Record<string, string>} */
const ASSIGN_WIRE_TO_UI = { A: 'yes', C: 'no', N: 'no' };
/** @type {Record<string, string>} */
const ASSIGN_UI_TO_WIRE = { yes: 'A', no: 'N' };

/** @param {any} text @returns {Record<number, number|null>} */
function lettersToPointers(text) {
  if (!text) return { 1: null, 2: null, 3: null, 4: null };
  const letters = String(text)
    .toUpperCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  /** @type {Record<number, number|null>} */
  const result = { 1: null, 2: null, 3: null, 4: null };
  letters.slice(0, 4).forEach((letter, i) => {
    const code = letter.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
    if (code >= 1 && code <= 12) result[i + 1] = code;
  });
  return result;
}

/**
 * @param {number|null|undefined} p1
 * @param {number|null|undefined} p2
 * @param {number|null|undefined} p3
 * @param {number|null|undefined} p4
 */
function pointersToLetters(p1, p2, p3, p4) {
  return [p1, p2, p3, p4]
    .filter((n) => typeof n === 'number' && n >= 1 && n <= 12)
    .map((n) => String.fromCharCode('A'.charCodeAt(0) + /** @type {number} */ (n) - 1))
    .join(',');
}

/**
 * Wire → UI.
 * @param {Partial<ClaimDetail>} wire
 * @returns {any}
 */
export function toUiClaim(wire) {
  /** @type {any} */
  const ui = { ...wire };

  if ('patient_relationship_to_insured' in wire) {
    ui.subscriber_relationship = REL_WIRE_TO_UI[/** @type {string} */ (wire.patient_relationship_to_insured)] || '';
    delete ui.patient_relationship_to_insured;
  }
  if ('claim_frequency_code' in wire) {
    ui.claim_mode = FREQ_WIRE_TO_UI[/** @type {string} */ (wire.claim_frequency_code)] || 'new';
    delete ui.claim_frequency_code;
  }
  if ('release_of_information' in wire) {
    ui.signature_on_file = wire.release_of_information === 'Y';
    delete ui.release_of_information;
  }
  if ('benefits_assignment' in wire) {
    ui.insured_signature_on_file = wire.benefits_assignment === 'Y';
    delete ui.benefits_assignment;
  }
  if ('accept_assignment' in wire) {
    ui.accept_assignment = ASSIGN_WIRE_TO_UI[/** @type {string} */ (wire.accept_assignment)] || 'no';
  }
  if ('outside_lab' in wire) {
    ui.outside_lab = wire.outside_lab ? 'yes' : 'no';
  }
  if ('outside_lab_charge' in wire) {
    ui.outside_lab_charges = wire.outside_lab_charge;
    delete ui.outside_lab_charge;
  }
  if ('has_secondary_coverage' in wire) {
    ui.other_health_benefit_plan = wire.has_secondary_coverage ? 'yes' : 'no';
    delete ui.has_secondary_coverage;
  }
  if ('hospitalization_from_date' in wire) {
    ui.hospitalization_from = wire.hospitalization_from_date;
    delete ui.hospitalization_from_date;
  }
  if ('hospitalization_to_date' in wire) {
    ui.hospitalization_to = wire.hospitalization_to_date;
    delete ui.hospitalization_to_date;
  }
  if ('disability_from_date' in wire) {
    ui.unable_work_from = wire.disability_from_date;
    delete ui.disability_from_date;
  }
  if ('work_return_date' in wire) {
    ui.unable_work_to = wire.work_return_date;
    delete ui.work_return_date;
  }

  if (Array.isArray(wire.service_lines)) {
    ui.line_items = wire.service_lines.map((sl) => {
      const line = {
        ...sl,
        procedure_code: sl.cpt_hcpcs_code || '',
        total_charge: sl.charge_amount || '',
        diagnosis_pointer_text: pointersToLetters(
          sl.diagnosis_pointer_1, sl.diagnosis_pointer_2,
          sl.diagnosis_pointer_3, sl.diagnosis_pointer_4,
        ),
      };
      delete line.cpt_hcpcs_code;
      delete line.charge_amount;
      delete line.diagnosis_pointer_1;
      delete line.diagnosis_pointer_2;
      delete line.diagnosis_pointer_3;
      delete line.diagnosis_pointer_4;
      return line;
    });
    delete ui.service_lines;
  }

  return ui;
}

/**
 * UI → Wire.
 * @param {any} ui
 * @returns {any}
 */
export function fromUiClaim(ui) {
  /** @type {any} */
  const wire = { ...ui };

  if ('subscriber_relationship' in ui) {
    wire.patient_relationship_to_insured = REL_UI_TO_WIRE[ui.subscriber_relationship] || '18';
    delete wire.subscriber_relationship;
  }
  if ('claim_mode' in ui) {
    wire.claim_frequency_code = FREQ_UI_TO_WIRE[ui.claim_mode] || '1';
    delete wire.claim_mode;
  }
  if ('signature_on_file' in ui) {
    wire.release_of_information = ui.signature_on_file ? 'Y' : 'N';
    delete wire.signature_on_file;
  }
  if ('insured_signature_on_file' in ui) {
    wire.benefits_assignment = ui.insured_signature_on_file ? 'Y' : 'N';
    delete wire.insured_signature_on_file;
  }
  if ('accept_assignment' in ui) {
    wire.accept_assignment = ASSIGN_UI_TO_WIRE[ui.accept_assignment] || 'N';
  }
  if ('outside_lab' in ui) {
    wire.outside_lab = ui.outside_lab === 'yes';
  }
  if ('outside_lab_charges' in ui) {
    wire.outside_lab_charge = ui.outside_lab_charges;
    delete wire.outside_lab_charges;
  }
  if ('other_health_benefit_plan' in ui) {
    wire.has_secondary_coverage = ui.other_health_benefit_plan === 'yes';
    delete wire.other_health_benefit_plan;
  }
  if ('hospitalization_from' in ui) {
    wire.hospitalization_from_date = ui.hospitalization_from;
    delete wire.hospitalization_from;
  }
  if ('hospitalization_to' in ui) {
    wire.hospitalization_to_date = ui.hospitalization_to;
    delete wire.hospitalization_to;
  }
  if ('unable_work_from' in ui) {
    wire.disability_from_date = ui.unable_work_from;
    delete wire.unable_work_from;
  }
  if ('unable_work_to' in ui) {
    wire.work_return_date = ui.unable_work_to;
    delete wire.unable_work_to;
  }

  if (Array.isArray(ui.line_items)) {
    wire.service_lines = ui.line_items.map((/** @type {any} */ line) => {
      const ptrs = lettersToPointers(line.diagnosis_pointer_text);
      const sl = {
        ...line,
        cpt_hcpcs_code: line.procedure_code || '',
        charge_amount: line.total_charge || '',
        diagnosis_pointer_1: ptrs[1],
        diagnosis_pointer_2: ptrs[2],
        diagnosis_pointer_3: ptrs[3],
        diagnosis_pointer_4: ptrs[4],
      };
      delete sl.procedure_code;
      delete sl.total_charge;
      delete sl.diagnosis_pointer_text;
      return sl;
    });
    delete wire.line_items;
  }

  return wire;
}
